import { yupResolver } from "@hookform/resolvers/yup";
import Button from '@mui/material/Button';
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FieldValues } from "react-hook-form/dist/types";
import { FormControl, FormHelperText, InputAdornment, InputLabel, OutlinedInput } from "@mui/material";
import { useMoeda } from '../../hooks/useMoeda';
import { useDataHora } from '../../hooks/useDataHora';
import * as yup from "yup";
import { validarMoedaReal } from '../../utils/validarMoedaReal';

interface ModalAbrirCaixaProps {
    fecharFormulario: () => void;
    formularioAberto: boolean;
    abrirCaixa: (descricao: string, valorInicial: number) => Promise<boolean | null>;
    ultimoCaixa?: { valorFinal: number } | null;
}

const schema = yup.object().shape({
    descricao: yup.string().required('A descrição é obrigatória'),
    valorInicial: yup.string().test('valorInicial', 'Valor inválido', (valor) => {
        return !validarMoedaReal(valor, 0.00, undefined, true);
    }),
});

export const ModalAbrirCaixa = ({ formularioAberto, fecharFormulario, abrirCaixa, ultimoCaixa }: ModalAbrirCaixaProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const { number } = useMoeda();
    const { dataHoraBr } = useDataHora();

    const { reset, register, handleSubmit, formState: { errors }, setValue, watch } = useForm({
        defaultValues: {
            descricao: '',
            valorInicial: '0'
        },
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    const handleAbrirCaixa = async (dados: FieldValues) => {
        setLoading(true);

        const result = await abrirCaixa(
            dados.descricao.trim(),
            number(dados.valorInicial)
        );
    
        setLoading(false);
        if (result) {
            handleFecharFormulario();
        }
    }

    const handleFecharFormulario = () => {
        reset({
            descricao: '',
            valorInicial: '0'
        });
        fecharFormulario();
    }

    useEffect(() => {
        if (formularioAberto) {
            const descricaoInicial = `Intervalo de caixa do dia ${dataHoraBr()}`;
            const valorInicial = ultimoCaixa?.valorFinal ?? 0;
            reset({
                descricao: descricaoInicial,
                valorInicial: String(valorInicial)
            });
        }
    }, [formularioAberto, ultimoCaixa, reset]);

    return (
        <Dialog fullWidth open={formularioAberto} onClose={handleFecharFormulario}>
            <DialogTitle>Abrir Caixa</DialogTitle>
            <form onSubmit={handleSubmit(handleAbrirCaixa)}>
                <DialogContent>
                    <FormControl fullWidth>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="descricao-caixa"
                            label="Descrição"
                            type="text"
                            fullWidth
                            value={watch('descricao')}
                            {...register("descricao")}
                            error={!!errors.descricao}
                            helperText={<>{errors.descricao?.message}</>}
                        />
                    </FormControl>
                    <FormControl fullWidth sx={{ mt: 3 }}>
                        <InputLabel error={!!errors.valorInicial} htmlFor="valor-inicial-caixa">Valor Inicial</InputLabel>
                        <OutlinedInput
                            id="valor-inicial-caixa"
                            error={!!errors.valorInicial}
                            startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                            label="Valor Inicial"
                            fullWidth
                            value={watch('valorInicial')}
                            {...register("valorInicial")}
                        />
                        {!!errors.valorInicial && <FormHelperText error={true}><>{errors.valorInicial?.message}</></FormHelperText>}
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleFecharFormulario} variant="outlined">Cancelar</Button>
                    <Button type="submit" variant="contained">
                        {loading == false ? (
                            <>Abrir Caixa</>
                        ) : (
                            <CircularProgress color="inherit" />
                        )}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
